import * as React from 'react'
import {
  Box, Table, TableHead, TableBody, TableRow, TableCell,
  Checkbox, Chip, Typography, Button, TablePagination,
} from '@mui/material'
import AwbLinkCell from '../../../common/AwbLinkCell'
import {dataGridWrapSx, headerCellSx, cellSx, alertStripSx, primaryBtnSx, secondaryBtnSx} from '../../../styles/patterns'
import {FONT, SIZE, WEIGHT} from '../../../styles/foundation/typography'
import {NAVY, SUBTLE, PRIMARY_BLUE, SECONDARY_BLUE, INFO, WARNING, SUCCESS, ERROR, MUTED, PURPLE, BORDER, LIGHT_BG, WHITE, LOST_RED, withOpacity} from '../../../styles/foundation/colors'
import {columnDividerSx, declaredFigureSx, billedFigureSx, excessFigureSx, subtleTextSx, governSx, prefixSx, governPrefixSx, dimsSx} from './gridSx'
import {STATUS, DISPUTE_TYPE} from './data/reference'
import {fmtKg, fmtKgOrDash, fmtCurrency, deadline, dimsText, dimsSpoken} from './format'
import {evidenceScore, EVIDENCE_LEVEL_LABEL} from './state/evidenceScore'
import {governingType} from './state/query'
import EmptyState from './EmptyState'
import Lightbox from './Lightbox'
import {courierScanCount, courierScanSrcs} from './data/scanImage'

// One fixed colour per STATUS tone — see data/reference.js. Kept local to this
// grid because the tone vocabulary (info/ai/warn/neutral/ok/danger) only ever
// shows up on the status chip.
const TONE_COLOR = {
  info: INFO,
  ai: PURPLE,
  warn: WARNING,
  neutral: MUTED,
  ok: SUCCESS,
  danger: ERROR,
}

const DEADLINE_COLOR = {crit: ERROR, warn: WARNING, ok: SUCCESS}

// The shared headerCellSx is SIZE.xs (13.12px). Stepped down to the module's
// body size here so uppercase bold headers don't out-shout the values beneath
// them — overridden locally rather than in styles/patterns, which the whole app
// shares. Measured on the harness at /__weight-harness.
//
// Headers stay on ONE line — the shared pattern's whiteSpace: 'nowrap' is kept,
// not overridden. Letting "Excess weight" and "Excess freight" wrap did buy the
// table ~63px, but a two-line header strip reads badly, so the width is taken
// out of horizontal padding and the product column instead (see CELL_PX below).
const headerSx = {...headerCellSx, fontSize: SIZE['2xs']}

// Centred variant for the two columns whose content is centred rather than
// text-aligned — the courier image link and the action buttons. v1's grid does
// the same for its image columns (headerAlign/align 'center' in
// weight/list/discrepancy.js), so this matches the module it replaces.
const headerCenterSx = {...headerSx, textAlign: 'center'}

// One width for every action button so Accept and Dispute match each other and
// line up row to row. Sized to the widest label ("Dispute" at SIZE.xs semibold
// plus primaryBtnSx's px: 2) with room to spare, so no label ever wraps.
const ACTION_BTN_W = 92

// The Accept / Dispute pair. The shared primaryBtnSx / secondaryBtnSx put these
// two the wrong way round: measured on white, Accept's NAVY label came out at
// 14.35:1 — the loudest text anywhere in the row — while Dispute's white-on-
// PRIMARY_BLUE was the faintest at 3.78:1, under the 4.5:1 AA floor for 13px
// semibold. So the concession shouted and the action the module exists to
// produce whispered. Accept's 1px BORDER outline measured 1.29:1 on white too,
// i.e. invisible, which is why it read as floating text rather than a button.
//
// Accept: SUBTLE on a LIGHT_BG fill (4.67:1) — legibly second, and a visible
// surface so it reads as a control. MUTED was the obvious quieter grey and was
// rejected at 2.73:1.
//
// Accept on hover/focus: LOST_RED (6.86:1). Conceding costs the merchant money
// and, once confirmed, the row leaves this tab with no undo reachable from the
// grid — so the cost is named at the moment of intent rather than as constant
// alarm on every row. LOST_RED is borrowed from the 'Lost' shipment status in
// dashboard/StatusTree.js, which is the same idea. It is the only red in the
// foundation that clears AA: ERROR is 3.62:1 and RED is 4.23:1, both failing.
//
// It is deliberately on :focus-visible as well as :hover. A warning that only
// exists on hover does not exist for keyboard or touch users, which is exactly
// the defect the review flagged against the EXCESS_NOTE tooltips.
// Spelled as two separate keys, both matching secondaryBtnSx's own '&:hover'
// key exactly, so the object literal OVERWRITES it. Writing them as a combined
// '&:hover, &:focus-visible' selector would be a different key, leaving the
// inherited blue hover emitted alongside this one and the outcome decided by
// CSS source order.
// `headerCellSx` uppercases every header. "Wt." is an abbreviation, not a word
// being shouted, so it opts out of that transform while the header word beside
// it keeps the uppercase treatment the other nine columns share. The cell's
// textContent is unaffected, so the accessible name stays "Declared Wt.".
const wtSx = {textTransform: 'none'}

const ACCEPT_WARN_STATE = {
  color: LOST_RED,
  borderColor: LOST_RED,
  bgcolor: withOpacity(LOST_RED, 0.06),
}

const acceptBtnSx = {
  ...secondaryBtnSx,
  color: SUBTLE,
  bgcolor: LIGHT_BG,
  '&:hover': ACCEPT_WARN_STATE,
  '&:focus-visible': ACCEPT_WARN_STATE,
}

// Dispute: SECONDARY_BLUE (4.68:1) instead of PRIMARY_BLUE (3.78:1). Both are
// foundation tokens, so this introduces no new colour. NOTE: PRIMARY_BLUE fails
// AA on every primary button in the panel, not just this one — this override is
// local, so the underlying token still wants fixing centrally.
const disputeBtnSx = {
  ...primaryBtnSx,
  bgcolor: SECONDARY_BLUE,
  '&:hover': {bgcolor: SECONDARY_BLUE, filter: 'brightness(0.92)'},
}

// Product was the widest column in the table at 237px, and the only one whose
// width was pure preference rather than a floor — the SKU code is the row's
// real identifier and fits inside 140px, while the marketing title below it was
// already ellipsised at any width. Trimming it is what pays for keeping every
// header on one line.
//
// 150 fits the short coded SKUs ("1M-BWJG-R0149-NB5" needs 146px) in full. The
// long descriptive ones ("NBSS24MC10-Men-Coord Set-Navy Blue-L" needs 285px)
// still ellipsise — but they did at the old 220 too, so nothing regressed, and
// the title attribute on both lines gives the full string on hover.
const PRODUCT_MAX_W = 150

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100]

// TablePagination puts its font size on several sub-elements rather than
// inheriting, so each one has to be named — the same list ProzoDataGrid spells
// out at common/ProzoDataGrid.js:85-88, kept at the module's own 2xs.
const paginationSx = {
  fontFamily: FONT,
  fontSize: SIZE['2xs'],
  borderTop: `1px solid ${BORDER}`,
  '& .MuiTablePagination-toolbar': {minHeight: 44, px: 1.5},
  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select': {
    fontFamily: FONT,
    fontSize: SIZE['2xs'],
    color: SUBTLE,
  },
}

// A floor that must stay BELOW the table's natural minimum, or it stops being a
// floor and becomes a stretcher. Measured: with Evidence dropped from Action
// Required and the density pass applied, the table's true min-content is
// 1107px — but minWidth was still 1180, forcing it 73px wider than it needed to
// be and pushing Courier image and Action past the right edge of the card in the
// real app, which is narrower than this dev harness by the width of the sidebar.
//
// The insurance minWidth was added for is largely already provided by the table
// itself: every header and every weight figure is nowrap, so min-content is
// rigid and the columns cannot crush however narrow the container gets — the
// table simply overflows and dataGridWrapSx's overflow:'auto' scrolls it. This
// value is kept only as a guard for a future change that makes something
// wrappable, and is deliberately well under 1107 so it never binds.
const TABLE_MIN_W = 1040

// Column rules and cell padding come from gridSx.js so this table and
// SkuIntelligence's cannot drift. The leading checkbox column additionally
// clears its rule here: it belongs to the row rather than the data, so a
// divider between it and Courier & AWB would separate a row from itself.
const tableSx = {
  minWidth: TABLE_MIN_W,
  ...columnDividerSx,
  // Density, measured rather than guessed. Two things set the row height: the
  // Action cell's stacked buttons (77px of content) and the Declared/Billed
  // cells' four stacked lines (71px). Type size barely moves either — leading
  // and control height do.
  //
  // The lineHeight has to name the Typography descendants, not the cell: MUI
  // Typography carries its own lineHeight (1.5), which wins over anything
  // inherited from the td, so setting it on the cell did nothing at all.
  '& tbody td': {py: 0.75},
  '& tbody td .MuiTypography-root': {lineHeight: 1.3},
  '& th:first-of-type, & td:first-of-type': {borderRight: 'none'},
}

// The Evidence chip's colour per level. The wording comes from
// state/evidenceScore.js, which the .xlsx export reads too, so the sheet's
// Evidence column and this chip can never grade a shipment in different
// words; only the colour, which a spreadsheet has no use for, lives here.
const EVIDENCE_TONE = {
  none: {color: ERROR, label: EVIDENCE_LEVEL_LABEL.none},
  weak: {color: WARNING, label: EVIDENCE_LEVEL_LABEL.weak},
  strong: {color: SUCCESS, label: EVIDENCE_LEVEL_LABEL.strong},
}

const chipSx = (color) => ({
  bgcolor: withOpacity(color, 0.12),
  color,
  fontFamily: FONT,
  fontWeight: WEIGHT.semibold,
  fontSize: SIZE['2xs'],
  height: 22,
})


/**
 * The Billed cell spoken aloud. Exported so the governing-weight signal stays
 * assertable in tests: on screen it is carried only by bold-vs-muted styling,
 * which Testing Library cannot check without the node access this repo's lint
 * rules forbid. Naming the governing side in words keeps both the screen reader
 * and the test suite honest about a distinction the eye gets from font weight.
 */
export function billedSummary(row, governing) {
  const label = DISPUTE_TYPE[governing] ? DISPUTE_TYPE[governing].label : governing
  return [
    `Billed ${fmtKg(row.appliedWeight)}`,
    `dead weight ${fmtKgOrDash(row.deadKg)}`,
    `volumetric ${fmtKgOrDash(row.volKg)}`,
    dimsSpoken(row.dims),
    `governed by ${label}`,
  ].join(', ')
}

/**
 * The Declared cell spoken aloud. The volumetric slot renders as an em dash,
 * which a screen reader announces as nothing at all or as stray punctuation —
 * so the reason for the gap is said in words here instead. Exported alongside
 * billedSummary for the same reason: it keeps the claim assertable in tests.
 */
/**
 * "30×20×10 cm", or null when the dimensions are not on the record.
 *
 * data/api.js maps dims and declaredDims to null for legacy discrepancies raised before the
 * dead/volumetric split was captured, on the stated assumption that the grid rendered them
 * defensively. It did not: four places dereferenced .l directly, so the first legacy row in a tab
 * took the whole page down with "Cannot read properties of null". The demo dataset always carries
 * dimensions, which is why every test passed.
 */
// Lives in format.js so the grid, the drawer, the SKU table and the export cannot disagree about
// which records have dimensions. Re-exported because callers already import it from here.
export {dimsText}

export function declaredSummary(row) {
  return [
    `Declared ${fmtKg(row.originalWeight)}`,
    `dead weight ${fmtKg(row.originalWeight)}`,
    `volumetric ${fmtKg(row.declaredVolKg)}`,
    dimsSpoken(row.declaredDims),
  ].join(', ')
}

// The DEAD / VOL prefixes in the Billed cell: deliberately quieter and a step
// smaller than the figures they label, so the eye lands on the weight rather
// than the word. Letter-spaced because uppercase at 11px sets very tightly.

// The governing side gets a small amber tag. Amber rather than red because
// this is the reason for the charge, not an error — it matches the "at risk"
// KPI card, which is the same idea one level up. Only ONE of the two is ever
// tinted, so the colour is a pointer rather than decoration: it answers "which
// number do I have to argue with" at a glance, across ten rows.
//
// Amber TINT with NAVY text, never amber text: amber-on-amber measured 1.51:1
// against the 4.5:1 WCAG AA minimum at this 11px size — legible on a good
// monitor, unreadable to plenty of people. The app's own alertStripSx pairs a
// tint with NAVY for exactly this reason.

// Column set keyed by tab (B1). Courier images took the slot Status used to
// hold, because on the two tabs a merchant actually works from, Status told
// them nothing: every Action Required row is NEW, and every In Dispute row is
// DISPUTED_BY_*, which the tab name already says. The courier's own photo of
// the parcel is the evidence that decides whether to accept the charge or
// contest it, so it earns the column that a constant-value chip did not.
//
// Resolved is the exception and keeps both. It is the one tab whose status
// genuinely varies — accepted by courier, rejected by courier, auto accepted,
// accepted by merchant — and that outcome is the entire reason to open the
// tab. Dropping it there would have cost real information to satisfy a rule
// that only made sense where the value was constant.
const ALL_COLUMNS = [
  'shipment', 'product', 'declared', 'billed',
  'excessWeight', 'excessAmount', 'evidence', 'deadline', 'courierImage', 'action',
]

// Evidence is dropped on Action Required for the same reason Status was: it is
// constant there. A row only reaches this tab as NEW, and nothing in the queue
// carries merchant evidence until the merchant attaches some — so the column
// printed "No evidence" on every row and cost width to say nothing. It stays on
// In Dispute and Resolved, where the merchant has since attached evidence and
// the value genuinely varies.
// Deadline is dropped on In Dispute and Resolved. It counts down to auto-accept
// — the moment the system concedes the charge because the merchant did not — so
// it only means anything while acting is still possible. On In Dispute the case
// is already filed and the clock has stopped; on Resolved it settled long ago.
// Leaving it there showed a countdown to an event that can no longer happen.
// Auto Accepted is a closed tab too, so it reads exactly like Resolved.
function columnsForTab(tab) {
  if (tab === 'action') return ALL_COLUMNS.filter((c) => c !== 'evidence')
  if (tab === 'dispute') return ALL_COLUMNS.filter((c) => c !== 'deadline')
  if (tab === 'resolved' || tab === 'auto') {
    return ALL_COLUMNS
      .filter((c) => c !== 'deadline')
      .flatMap((c) => (c === 'action' ? ['status', c] : c))
  }
  return ALL_COLUMNS
}

/**
 * Plain MUI Table (not ProzoDataGrid) — see task brief: Shipment, Product,
 * Declared/Billed and Deadline/Status are multi-line composites that
 * DataGrid's single-line cell model fights.
 *
 * `rows` is the already-paginated page slice (paginate() from state/query.js).
 * `total` describes the *full* filtered set (every page, this
 * tab, current filters) so the selection banner can offer "select all N
 * matching" without the grid needing to know how filtering works. `tab`
 * selects the column set (B1/B4) — see columnsForTab above.
 */
export default function DisputeGrid({
  rows, page, perPage, total, onPageChange, onPerPageChange,
  selection, onToggleSelect, onSelectAll, onClearSelection,
  onOpenRow, isTabEmpty, onClearFilters, tab, onAct,
  acceptableCount = 0, onAcceptSelected,
}) {
  // The courier scan viewer. Local state: the grid is its only trigger and the
  // dialog is purely presentational, so plumbing it through index.js would add
  // a prop chain that nothing else reads. Holds the ROW, not a src, so the
  // viewer can page through a row's scans without recomputing which row it is.
  const [scanRow, setScanRow] = React.useState(null)
  const [scanIdx, setScanIdx] = React.useState(0)
  const scanCount = courierScanCount(scanRow)
  const openScans = (row) => { setScanRow(row); setScanIdx(0) }

  const columns = columnsForTab(tab)
  const showStatus = columns.includes('status')
  const showEvidence = columns.includes('evidence')
  const showDeadline = columns.includes('deadline')
  const pageIds = React.useMemo(() => rows.map((r) => r.id), [rows])
  const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => selection.has(id))
  const someOnPageSelected = pageIds.some((id) => selection.has(id))
  const pages = Math.max(1, Math.ceil((total || 0) / (perPage || 1)))

  if (rows.length === 0) {
    return (
      <Box sx={dataGridWrapSx}>
        <EmptyState variant={isTabEmpty ? 'tab-empty' : 'no-match'} onClearFilters={onClearFilters} />
      </Box>
    )
  }

  return (
    <Box sx={dataGridWrapSx}>
      {selection.size > 0 && (
        <Box sx={{...alertStripSx(PRIMARY_BLUE), justifyContent: 'space-between', borderRadius: 0, borderLeft: 'none', borderRight: 'none', borderTop: 'none'}}>
          <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold}}>
            {selection.size} selected
          </Typography>
          {/* flexWrap so an added action can never push this strip wider than
              the table beneath it — the grid's fit is tight by design and the
              strip shares its horizontal scroll container. */}
          <Box sx={{display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end'}}>
            {/* Absent, not disabled, when nothing in the selection can be
                accepted — on In Dispute and Resolved that is every row, and a
                permanently greyed control there would only invite the click it
                cannot honour. When only part of the selection qualifies the
                label says so, because the alternative is a button that reads
                "N selected" and quietly acts on fewer.

                Styled as a filled primary (disputeBtnSx), on request. Note
                what that trades: the per-row Accept stays quiet with a LOST_RED
                hover because conceding a charge costs the merchant money, and
                this button now carries the opposite emphasis while doing the
                same thing to a whole page at once. The confirmation dialog is
                what stands between the click and the consequence. */}
            {acceptableCount > 0 && onAcceptSelected && (
              <Button
                size='small'
                onClick={onAcceptSelected}
                sx={{...disputeBtnSx, fontSize: SIZE['2xs'], py: 0.5, whiteSpace: 'nowrap'}}
              >
                {acceptableCount === selection.size
                  ? `Accept charge on ${acceptableCount} selected`
                  : `Accept charge on ${acceptableCount} of ${selection.size} selected`}
              </Button>
            )}
            <Button
              size='small'
              onClick={onClearSelection}
              sx={{fontFamily: FONT, fontSize: SIZE['2xs'], textTransform: 'none', color: SUBTLE}}
            >
              Clear selection
            </Button>
          </Box>
        </Box>
      )}

      <Table sx={tableSx}>
        <TableHead>
          <TableRow>
            <TableCell sx={headerSx} padding='checkbox'>
              <Checkbox
                size='small'
                checked={allOnPageSelected}
                indeterminate={someOnPageSelected && !allOnPageSelected}
                onChange={() => onSelectAll(pageIds, !allOnPageSelected)}
                inputProps={{'aria-label': 'Select all rows on this page'}}
              />
            </TableCell>
            <TableCell sx={headerSx}>Courier &amp; AWB</TableCell>
            <TableCell sx={headerSx}>Product</TableCell>
            {/* The three standalone figures are centred; Billed stays left
                because it is a multi-line composite (figure, then the DEAD/VOL
                pair, then dims) whose internal alignment would scramble. */}
            <TableCell sx={headerSx}>Declared <Box component='span' sx={wtSx}>Wt.</Box></TableCell>
            <TableCell sx={headerSx}>Billed <Box component='span' sx={wtSx}>Wt.</Box></TableCell>
            <TableCell sx={headerCenterSx}>Excess weight</TableCell>
            <TableCell sx={headerCenterSx}>Excess freight</TableCell>
            {showEvidence && <TableCell sx={headerSx}>Evidence</TableCell>}
            {showDeadline && <TableCell sx={headerSx}>Deadline</TableCell>}
            {/* "Image", not "Courier image". Measured: the column was 116px wide
                to fit its own header while its content — the word "View", or an
                em dash — needs 31px. That was 85px of header text on a table
                fighting for horizontal room, the single largest
                width-for-nothing left. The link's accessible name still reads
                "View 1 courier image for AWB ...", so a screen reader loses
                nothing. */}
            {/* The courier's image, not the merchant's: "Images Only" in Evidence is the merchant's side. */}
            <TableCell sx={headerCenterSx}>Courier image</TableCell>
            {showStatus && <TableCell sx={headerCenterSx}>Status</TableCell>}
            <TableCell sx={headerCenterSx}>Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            const statusDef = STATUS[row.status] || {}
            const tone = TONE_COLOR[statusDef.tone] || NAVY
            const dl = deadline(row.deadlineAt)
            const evidence = evidenceScore(row)
            const evidenceTone = EVIDENCE_TONE[evidence.level]
            const sameAsSku = !row.product || row.product === row.sku
            const isNew = row.status === 'NEW'

            // B0's derived type — never row.type, see governingType's doc
            // comment — decides which of Dead/Vol renders as the governing
            // (bold) figure below. Its DISPUTE_TYPE label is what the drawer,
            // the triage grouping and the export all print for the same row,
            // so no two surfaces can describe a row differently.
            const gType = governingType(row)
            const deadGoverns = gType === 'HIGHER_DEAD' || gType === 'HIGHER_BOTH'
            const volGoverns = gType === 'HIGHER_VOL' || gType === 'HIGHER_BOTH'

            return (
              <TableRow key={row.id} hover selected={selection.has(row.id)}>
                <TableCell sx={cellSx} padding='checkbox'>
                  <Checkbox
                    size='small'
                    checked={selection.has(row.id)}
                    onChange={() => onToggleSelect(row.id)}
                    inputProps={{'aria-label': `Select dispute ${row.id}`}}
                  />
                </TableCell>

                {/* Courier on top, then the AWB, then the service — the
                    requested layout. The AWB uses the app's own AwbLinkCell
                    (common/AwbLinkCell.js) rather than a hand-rolled blue
                    Typography: that component is what standardises AWB links
                    to link-blue across the panel's tables, and it makes the
                    number a real link to /order-detail plus the copy icon,
                    which the hand-rolled version was not. `orderId` moves to
                    a title tooltip — the reference layout is three lines and
                    a fourth would re-introduce the wrapping this column had. */}
                <TableCell sx={cellSx}>
                  <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.xs, color: NAVY}}>
                    {row.courier}
                  </Typography>
                  <AwbLinkCell awb={row.awb} sx={{fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.semibold}} />
                  <Typography sx={subtleTextSx} title={`Order ${row.orderId}`}>
                    {row.service}
                  </Typography>
                </TableCell>

                <TableCell sx={cellSx}>
                  <Typography
                    title={row.sku}
                    sx={{
                      fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.xs, color: NAVY,
                      maxWidth: PRODUCT_MAX_W, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}
                  >
                    {row.sku}
                  </Typography>
                  {!sameAsSku && (
                    <Typography
                      title={row.product}
                      sx={{
                        ...subtleTextSx, maxWidth: PRODUCT_MAX_W, overflow: 'hidden',
                        textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}
                    >
                      {row.product}
                    </Typography>
                  )}
                </TableCell>

                {/* Declared mirrors Billed's shape so the two cells read line
                    for line — headline against headline, Dead against Dead,
                    Vol against Vol — which is the comparison this column pair
                    exists to make. Left-aligned for the same reason: Billed is
                    left, and a centred Declared put the two sets of figures on
                    different axes, so the eye had to travel to compare them.
                    Centring suited this cell when it held one lone number; it
                    stopped suiting it once it held four lines.
                    Dead is the weight the merchant put on the scale;
                    Vol is derived from the carton they declared.
                    Neither side is highlighted: the governing treatment on
                    Billed marks which of two measurements WON the billing, and
                    the declared side is a claim, not a contest. By construction
                    the declared volumetric always lands below the declared
                    weight, so dead would win every row and the tag would carry
                    no information — see the generator's declaredBox(). */}
                <TableCell sx={cellSx} aria-label={declaredSummary(row)}>
                  <Typography sx={declaredFigureSx}>
                    {fmtKg(row.originalWeight)}
                  </Typography>
                  <Box sx={{display: 'grid', gridTemplateColumns: 'auto auto', columnGap: 0.75, justifyContent: 'start', alignItems: 'baseline', mt: 0.25}}>
                    <Typography sx={prefixSx}>Dead Wt.</Typography>
                    <Typography sx={{...subtleTextSx, whiteSpace: 'nowrap'}}>{fmtKg(row.originalWeight)}</Typography>
                    <Typography sx={prefixSx}>Vol Wt.</Typography>
                    <Typography sx={{...subtleTextSx, whiteSpace: 'nowrap'}}>{fmtKg(row.declaredVolKg)}</Typography>
                  </Box>
                  {dimsText(row.declaredDims) && (
                    <Typography sx={dimsSx}>{dimsText(row.declaredDims)}</Typography>
                  )}
                </TableCell>

                {/* The visual design carries "which weight governs" purely by
                    weight and colour — bold navy for the governing side, muted
                    for the other. That is invisible to a screen reader, and it
                    replaced a text caption that used to say it outright, so the
                    whole breakdown is restated here as an accessible label.
                    Without this, removing the caption would have been a real
                    a11y regression rather than a layout tidy-up. */}
                <TableCell sx={cellSx} aria-label={billedSummary(row, gType)}>
                  <Typography sx={billedFigureSx}>
                    {fmtKg(row.appliedWeight)}
                  </Typography>
                  {/* DEAD / VOL as small uppercase prefixes with the figure
                      beside each, then the dimensions on their own line — the
                      layout requested from the drawer's Package block. Laid out
                      on a 2-column grid so the two figures align on the same x
                      regardless of prefix width, and `whiteSpace: nowrap` on
                      the row keeps "0.90 kg" from breaking after the number,
                      which is what made the old flex version wrap to four
                      lines. The governing side is bold navy and the other muted
                      — that carries the dead-vs-volumetric story on its own, so
                      the separate "Higher Dead Weight" caption (which wrapped
                      to three lines at this column width) is gone. */}
                  <Box sx={{display: 'grid', gridTemplateColumns: 'auto auto', columnGap: 0.75, justifyContent: 'start', alignItems: 'baseline', mt: 0.25}}>
                    <Typography sx={deadGoverns ? governPrefixSx : prefixSx}>Dead Wt.</Typography>
                    <Typography sx={{...(deadGoverns ? governSx : subtleTextSx), whiteSpace: 'nowrap'}}>
                      {fmtKgOrDash(row.deadKg)}
                    </Typography>
                    <Typography sx={volGoverns ? governPrefixSx : prefixSx}>Vol Wt.</Typography>
                    <Typography sx={{...(volGoverns ? governSx : subtleTextSx), whiteSpace: 'nowrap'}}>
                      {fmtKgOrDash(row.volKg)}
                    </Typography>
                  </Box>
                  {dimsText(row.dims) && (
                    <Typography sx={dimsSx}>{dimsText(row.dims)}</Typography>
                  )}
                </TableCell>

                {/* No EXCESS_NOTE tooltip on either figure. It fired on all ten
                    rows with identical text, and being hover-only it never
                    reached a keyboard or touch user anyway — the review flagged
                    exactly that. The disclaimer is not lost: the drawer states
                    it as visible prose beside the same figure, which is where
                    someone deciding whether to dispute actually reads it. */}
                <TableCell sx={{...cellSx, textAlign: 'center'}}>
                  <Typography sx={excessFigureSx}>
                    +{fmtKg(row.excessKg)}
                  </Typography>
                </TableCell>

                <TableCell sx={{...cellSx, textAlign: 'center'}}>
                  <Typography sx={excessFigureSx}>
                    {fmtCurrency(row.excessAmount)}
                  </Typography>
                </TableCell>

                {showEvidence && (
                  <TableCell sx={cellSx}>
                    <Chip size='small' label={evidenceTone.label} sx={chipSx(evidenceTone.color)} />
                  </TableCell>
                )}

                {showDeadline && (
                  <TableCell sx={cellSx}>
                    <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold, color: DEADLINE_COLOR[dl.level]}}>
                      {dl.label}
                    </Typography>
                  </TableCell>
                )}

                {/* The courier's own photo of the parcel — the proof the charge
                    rests on, and the first thing a merchant checks before
                    deciding to accept or contest. Mirrors v1's Courier Images
                    column (weight/list/discrepancy.js): a quiet text link when
                    images exist, an em dash when they do not.
                    The label is a bare "View" — v1 prints the count too, but at
                    this column width the parenthetical read as noise on every
                    row. The count survives in the accessible name, which needs
                    row-distinguishing context anyway so that ten identical
                    "View" links are not announced identically.
                    Opens the scan viewer directly rather than the row drawer:
                    the merchant clicked an image link, so an image is what
                    should appear. The sources come from data/scanImage.js#courierScanSrcs
                    — the same helper the drawer's evidence section reads — so the grid
                    and the drawer always show the identical photo: the courier's real
                    one on a live row, the generated plate on a seeded one. */}
                <TableCell sx={{...cellSx, textAlign: 'center'}}>
                  {courierScanCount(row) > 0 ? (
                    <Button
                      size='small'
                      onClick={() => openScans(row)}
                      aria-label={`View ${courierScanCount(row)} courier ${courierScanCount(row) === 1 ? 'image' : 'images'} for AWB ${row.awb}`}
                      sx={{
                        fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold,
                        textTransform: 'none', p: 0, minWidth: 'auto', color: PRIMARY_BLUE,
                      }}
                    >
                      View
                    </Button>
                  ) : (
                    <Typography sx={{...subtleTextSx, color: MUTED}}>—</Typography>
                  )}
                </TableCell>

                {showStatus && (
                  <TableCell sx={{...cellSx, textAlign: 'center'}}>
                    <Chip size='small' label={statusDef.label || row.status} sx={chipSx(tone)} />
                  </TableCell>
                )}

                {/* Accept sits ABOVE Dispute on a NEW row, as requested.
                    availableActions() in actions.js already listed both for
                    NEW; only Dispute was surfaced here, so accepting a charge
                    meant opening the drawer first. Accept stays the QUIETER
                    of the two despite being on top — conceding the courier's
                    weight is the outcome this module exists to avoid, so it
                    should be reachable without being the obvious click.

                    The stack is a fixed-width column centred in the cell rather
                    than two width:100% buttons. Full-width buttons inherited the
                    cell's width, so the pair sat flush against the left edge and
                    changed size whenever the column did; a fixed width centres
                    them and keeps every row's buttons the same size and on the
                    same vertical line down the page. */}
                <TableCell sx={{...cellSx, textAlign: 'center'}}>
                  <Box sx={{display: 'inline-flex', flexDirection: 'column', gap: 0.5, width: ACTION_BTN_W}}>
                    {isNew && onAct && (
                      <Button size='small' onClick={() => onAct('accept', row)} sx={{...acceptBtnSx, py: 0.25}}>
                        Accept
                      </Button>
                    )}
                    {/* View is neutral navigation, not a concession, so it keeps
                        the plain secondary style — only Accept carries the cost
                        signal. */}
                    <Button
                      size='small'
                      onClick={() => onOpenRow(row)}
                      sx={{...(isNew ? disputeBtnSx : secondaryBtnSx), py: 0.25}}
                    >
                      {isNew ? 'Dispute' : 'View'}
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>

      {/* Rows-per-page + "Showing x–y of n" + prev/next, replacing the numbered
          page list. Matches ProzoDataGrid's footer (common/ProzoDataGrid.js),
          which is what every DataGrid-backed page in the panel shows, and the
          TablePagination shape freeze-v2 already uses.

          Two deliberate departures from ProzoDataGrid:

          It prints the REAL total. ProzoDataGrid falls back to "of many" because
          it paginates server-side and genuinely does not know the count; this
          grid holds every matching row in memory, so `total` is exact and
          "of many" would be throwing away information we have.

          It renders whenever there are rows, not only when pages > 1. The
          rows-per-page control has to stay reachable on a single page of
          results — otherwise picking 50 hides the control that would let you
          go back to 10. */}
      {total > 0 && (
        <TablePagination
          component='div'
          count={total}
          // TablePagination counts pages from 0; this grid counts from 1.
          page={Math.min(page, pages) - 1}
          onPageChange={(e, p) => onPageChange(p + 1)}
          rowsPerPage={perPage}
          rowsPerPageOptions={onPerPageChange ? ROWS_PER_PAGE_OPTIONS : [perPage]}
          onRowsPerPageChange={(e) => onPerPageChange && onPerPageChange(Number(e.target.value))}
          labelRowsPerPage='Rows per page:'
          labelDisplayedRows={({from, to, count}) => `Showing ${from}–${to} of ${count}`}
          sx={paginationSx}
        />
      )}

      {/* The courier scan viewer, reusing the same Lightbox the drawer and the
          billing record already open — one image dialog for the module rather
          than a second one that drifts. A row can carry more than one scan, so
          when it does the viewer pages between them; with a single scan the
          pager is not rendered at all. */}
      <Lightbox
        src={scanRow ? courierScanSrcs(scanRow)[scanIdx] || null : null}
        alt={scanRow ? `Courier weight scan ${scanIdx + 1} of ${scanCount} for AWB ${scanRow.awb}` : ''}
        open={Boolean(scanRow)}
        onClose={() => setScanRow(null)}
      />
      {scanRow && scanCount > 1 && (
        <Box sx={{position: 'fixed', bottom: 32, left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 1, zIndex: 1400}}>
          <Button
            size='small'
            disabled={scanIdx === 0}
            onClick={() => setScanIdx((i) => Math.max(0, i - 1))}
            sx={{...secondaryBtnSx, fontSize: SIZE['2xs'], bgcolor: WHITE}}
          >
            Previous
          </Button>
          <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: WHITE, alignSelf: 'center'}}>
            {scanIdx + 1} of {scanCount}
          </Typography>
          <Button
            size='small'
            disabled={scanIdx >= scanCount - 1}
            onClick={() => setScanIdx((i) => Math.min(scanCount - 1, i + 1))}
            sx={{...secondaryBtnSx, fontSize: SIZE['2xs'], bgcolor: WHITE}}
          >
            Next
          </Button>
        </Box>
      )}
    </Box>
  )
}
